import {
  Box,
  Button,
  Center,
  HStack,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { BudgetOrderByColumnEnum } from "@src/__generated__/graphql";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  NavBar,
} from "@src/components/ui-kit";
import { EditableText } from "@src/components/ui-kit/editable-text";
import { Icon } from "@src/components/ui-kit/Icon";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import type { BulkAction } from "@src/components/widgets/bulk-actions/types";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { useStore } from "@src/utils/hooks";
import type { CellContext } from "@tanstack/react-table";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { Fragment, useEffect, useLayoutEffect, useMemo } from "react";
import type { BudgetModel } from "../../projects/budgets/models/budget";
import { CreateBudgetMenu } from "./CreateBudgetMenu";
import {
  AccountManagerRenderer,
  ActionsRenderer,
  ApprovalRenderer,
  BrandRenderer,
  DateRenderer,
  PriceRenderer,
  ProjectRenderer,
  QuoteRenderer,
  StatusRenderer,
  VersionRenderer,
} from "./components";
import { getTypeOptions } from "./Store";

export type BudgetsTableProps = {
  onAssign?: (id: string) => void;
  returnParams: string;
  onDelete?: (id: string) => void;
  isAssigning?: boolean;
  projectId?: string;
  lowestPossibleDateForStartDateChange?: Date;
};

type BudgetExportTableRow =
  | {
      id: string;
      rowType: "parent";
      budget: BudgetModel;
      __subRows?: BudgetExportTableRow[];
    }
  | {
      id: string;
      rowType: "subrow";
      budget: BudgetModel;
    };

type BudgetExportCellContext = CellContext<BudgetExportTableRow, unknown>;

const isParentBudgetRow = (
  row: BudgetExportTableRow,
): row is Extract<BudgetExportTableRow, { rowType: "parent" }> =>
  row.rowType === "parent";

export const PROPOSALS_RETURM_PARAMS =
  'where={"AND"%3A[{"column"%3A"TYPE"%2C"value"%3A["UNASSIGNED"]%2C"operator"%3A"IN"}%2C{"AND"%3A[{"OR"%3A[]}]}]}&orderBy=[]&first=10&page=1';

export const BudgetListing = observer(function BudgetListing() {
  const { budgetsExportStore: store } = useStore();
  const { query } = useRouter();
  const bulkActions = useMemo<BulkAction[]>(
    () => [
      {
        id: "budgets-export-actions",
        label: () => t`Actions`,
        icon: "dots-vertical",
        isLoading: () => store.createQuoteMutator.isLoading.value,
        subActions: [
          {
            id: "create-quote",
            label: () => t`Create quote`,
            callback: (selectedIds: string[] | Set<string>) => {
              store.createQuote(Array.from(selectedIds));
            },
          },
        ],
      },
    ],
    [store],
  );

  useLayoutEffect(() => {
    if (!query) return;

    store.where.fromURLSearchParam(query);
    store.orderBy.fromURLSearchParam(query);
    store.pagination.fromURLSearchParam(query);
  }, []);

  useEffect(() => {
    store.fetchBudgets();
    store.setFilterOptions();
  }, []);

  const columns = useMemo<
    BodyColumnDef<BudgetExportTableRow, BudgetOrderByColumnEnum>[]
  >(
    () => [
      {
        id: "version",
        meta: { label: t`Version` },
        header: () => t`Version`,
        cell: ({ row }: BudgetExportCellContext) =>
          isParentBudgetRow(row.original) ? (
            <VersionRenderer version={row.original.budget.version} />
          ) : (
            <HStack>
              <VersionRenderer version={row.original.budget.version} />
              {!!row.original.budget.budget_name && (
                <Text>/ {row.original.budget.budget_name}</Text>
              )}
            </HStack>
          ),
      },
      {
        id: "description",
        meta: { label: t`Description` },
        header: () => t`Description`,
        size: 220,
        cell: ({ row }: BudgetExportCellContext) => (
          <EditableText
            field={row.original.budget.description}
            onConfirm={row.original.budget.handleUpdateDescription}
            isLoading={row.original.budget.isActionsLoading}
            isEditable={!row.original.budget.isActionsLoading}
            size="sm"
          />
        ),
      },
      {
        id: "brand",
        meta: { label: t`Brand` },
        header: () => t`Brand`,
        cell: ({ row }: BudgetExportCellContext) =>
          isParentBudgetRow(row.original) ? (
            <BrandRenderer row={row.original.budget} />
          ) : null,
      },
      {
        id: "project",
        meta: { label: t`Project` },
        header: () => t`Project`,
        cell: ({ row }: BudgetExportCellContext) =>
          isParentBudgetRow(row.original) ? (
            <ProjectRenderer row={row.original.budget} />
          ) : null,
      },
      {
        id: "account-manager",
        meta: { label: t`Account manager` },
        header: () => t`Account manager`,
        cell: ({ row }: BudgetExportCellContext) => (
          <AccountManagerRenderer
            accountManager={row.original.budget.account_manager}
          />
        ),
      },
      {
        id: "total",
        meta: { label: t`Total` },
        header: () => t`Total`,
        cell: ({ row }: BudgetExportCellContext) => (
          <PriceRenderer price={row.original.budget.price} />
        ),
      },
      {
        id: "approval",
        meta: { label: t`Approval` },
        header: () => (
          <VStack spacing="0">
            <Trans>Approval</Trans>
            <Text>
              <Trans>(Internal | Client)</Trans>
            </Text>
          </VStack>
        ),
        cell: ({ row }: BudgetExportCellContext) => (
          <ApprovalRenderer budget={row.original.budget} />
        ),
      },
      {
        id: "date",
        meta: { label: t`Date` },
        header: () => t`Date`,
        cell: ({ row }: BudgetExportCellContext) => (
          <DateRenderer date={new Date(row.original.budget.created_at)} />
        ),
      },
      {
        id: "status",
        meta: { label: t`Status` },
        header: () => t`Status`,
        cell: ({ row }: BudgetExportCellContext) => (
          <StatusRenderer status={row.original.budget.status} />
        ),
      },
      {
        id: "quotes",
        meta: { label: t`Quotes` },
        header: () => t`Quotes`,
        cell: ({ row }: BudgetExportCellContext) => (
          <QuoteRenderer budget={row.original.budget} />
        ),
      },
      {
        id: "budget_name",
        meta: { label: t`Name` },
        header: () => t`Name`,
        cell: ({ row }: BudgetExportCellContext) =>
          isParentBudgetRow(row.original) ? (
            <Text>{row.original.budget.budget_name ?? ""}</Text>
          ) : null,
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: () => t`Actions`,
        size: 100,
        cell: ({ row }: BudgetExportCellContext) => (
          <ActionsRenderer
            budget={row.original.budget}
            isSubRow={!isParentBudgetRow(row.original)}
            returnParams={store.returnParams}
            onDelete={store.handleDeleteBudget}
          />
        ),
      },
    ],
    [store],
  );

  const data = useMemo<BudgetExportTableRow[]>(
    () =>
      store.tableData.map((budget) => ({
        id: budget.id,
        rowType: "parent",
        budget,
        __subRows: budget.__subRows?.map((subrow, index) => ({
          id: `${budget.id}-${subrow.id}-${index}`,
          rowType: "subrow",
          budget: subrow,
        })),
      })),
    [store.tableData],
  );

  return (
    <Fragment>
      <DataTable>
        <NavBar
          justify="space-between"
          borderBottom="1px"
          borderColor="chakra-border-color"
          px="4"
        >
          <HStack h="full" spacing="0">
            <NavBar.Item
              active={!store.budgetTypeFilter?.value.length}
              onClick={() => store.budgetTypeFilter?.setValue([])}
            >
              <Trans>All</Trans>
            </NavBar.Item>
            {getTypeOptions().map(({ value, label }) => (
              <NavBar.Item
                key={value}
                active={store.budgetTypeFilter?.value[0] === value}
                onClick={() => {
                  store.budgetTypeFilter?.setValue(value);
                }}
              >
                {label}
              </NavBar.Item>
            ))}
          </HStack>
          <HStack spacing="3">
            <FiltersPanelRoot filters={store.where}>
              <HStack justify="space-between">
                <FiltersPanelFilters />
              </HStack>
            </FiltersPanelRoot>
            <CreateBudgetMenu
              queryParams={{
                returnParams: PROPOSALS_RETURM_PARAMS,
              }}
            >
              <Button colorScheme="violet" leftIcon={<Icon name="plus" />}>
                <Trans>New Budget</Trans>
              </Button>
            </CreateBudgetMenu>
          </HStack>
        </NavBar>
        <Box pos="relative">
          {store.fetching && (
            <Center
              pos="absolute"
              top="0"
              right="0"
              bottom="0"
              left="0"
              bg="blackAlpha.200"
              backdropBlur="3xl"
            >
              <Spinner />
            </Center>
          )}
          <DataTable.Body
            data={data}
            columns={columns}
            tableKey={store.tableKey}
            selected={Array.from(store.selectedIds)}
            onSelect={store.handleSelect}
            enableRowSelection
            enableSubRowSelection
            rowSelectionMode="subrowsOrEmptyParent"
            allIds={store.tableAllIds}
            bulkActions={bulkActions}
          />
        </Box>
        <Pagination store={store.pagination} />
      </DataTable>
      <div />
    </Fragment>
  );
});
